/**
 * previewStore — immutable preview revisions of a site, in a BlobStore (OPFS in
 * the browser, one bucket per site: `sites/<siteId>/_preview`).
 *
 * A preview build writes a NEW revision — every file under `rev/<rev>/`, then a
 * `.complete` marker — and only then switches the `current` pointer. Nothing is
 * rewritten in place, so a tab showing an older revision keeps loading its own
 * pages, stylesheet and assets while a newer build lands, and a build that
 * fails half-way leaves the previous pointer (and every tab on it) untouched.
 * The revision id is the content hash of the files, so rebuilding an unchanged
 * site reuses the revision it already has.
 *
 * Old revisions are collected after a switch, keeping the newest {@link KEEP}.
 */
import type { BlobStore } from "@website-builder/blob-store";

import type { BuildResult } from "./protocol.js";

/** Revisions kept after a switch (the current one always included). */
export const KEEP = 3;

const CURRENT = "current";
const HISTORY = "history.json";
const COMPLETE = ".complete";

export interface PreviewFile {
  readonly path: string;
  readonly content: string;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

const fileKey = (rev: string, path: string) => `rev/${rev}/${path}`;

/** Content hash of a set of files (order-independent), as a short hex id. */
export async function revisionOf(
  files: ReadonlyArray<PreviewFile>,
): Promise<string> {
  const sorted = [...files].sort((a, b) => (a.path < b.path ? -1 : 1));
  const joined = sorted.map((f) => `${f.path}\0${f.content}\0`).join("");
  const digest = await crypto.subtle.digest("SHA-256", enc.encode(joined));
  return Array.from(new Uint8Array(digest).slice(0, 8), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("");
}

/** Whether a revision was written completely (and so may be served). */
export function isComplete(store: BlobStore, rev: string): Promise<boolean> {
  return store.has(fileKey(rev, COMPLETE));
}

/**
 * Write a revision (idempotent: an already-complete one is left as is). The
 * marker goes last, so a crash mid-write leaves an incomplete revision that is
 * never pointed at.
 */
export async function writeRevision(
  store: BlobStore,
  files: ReadonlyArray<PreviewFile>,
  /** A caller that renders the id into the files hashes them beforehand. */
  revision?: string,
): Promise<string> {
  const rev = revision ?? (await revisionOf(files));
  if (!/^[0-9a-f]{16}$/.test(rev)) throw new Error(`bad revision id ${rev}`);
  if (await isComplete(store, rev)) return rev;
  for (const f of files)
    await store.put(fileKey(rev, f.path), enc.encode(f.content));
  await store.put(fileKey(rev, COMPLETE), new Uint8Array(0));
  return rev;
}

/** The revision previews currently open to, or null before the first one. */
export async function currentRevision(
  store: BlobStore,
): Promise<string | null> {
  const bytes = await store.get(CURRENT);
  return bytes ? dec.decode(bytes) : null;
}

async function history(store: BlobStore): Promise<string[]> {
  const bytes = await store.get(HISTORY);
  if (!bytes) return [];
  try {
    const v = JSON.parse(dec.decode(bytes)) as unknown;
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

/**
 * Point previews at a complete revision, then collect all but the newest
 * {@link KEEP}. Refuses an incomplete revision.
 */
export async function switchTo(store: BlobStore, rev: string): Promise<void> {
  if (!(await isComplete(store, rev)))
    throw new Error(`preview revision ${rev} is incomplete`);
  const next = [rev, ...(await history(store)).filter((r) => r !== rev)];
  await store.put(CURRENT, enc.encode(rev));
  await store.put(HISTORY, enc.encode(JSON.stringify(next.slice(0, KEEP))));
  for (const old of next.slice(KEEP))
    for (const key of await store.list(`rev/${old}/`)) await store.delete(key);
}

/** A file of a complete revision, or null (unknown revision or path). */
export async function readFile(
  store: BlobStore,
  rev: string,
  path: string,
): Promise<Blob | null> {
  if (!/^[0-9a-f]{16}$/.test(rev) || !(await isComplete(store, rev)))
    return null;
  return store.getBlob(fileKey(rev, path));
}

/** Build records kept for waiting pages to pick up. */
const KEEP_BUILDS = 10;

/** Record a build's outcome under the token its waiting page polls. */
export async function writeBuildResult(
  store: BlobStore,
  token: string,
  result: BuildResult,
): Promise<void> {
  const record = { ...result, at: Date.now() };
  await store.put(`build/${token}`, enc.encode(JSON.stringify(record)));
  const keys = await store.list("build/");
  if (keys.length <= KEEP_BUILDS) return;
  const dated = await Promise.all(
    keys.map(async (k) => {
      const b = await store.get(k);
      const at = b
        ? ((JSON.parse(dec.decode(b)) as { at?: number }).at ?? 0)
        : 0;
      return { k, at };
    }),
  );
  dated.sort((a, b) => b.at - a.at);
  for (const { k } of dated.slice(KEEP_BUILDS)) await store.delete(k);
}

/** A build's outcome, or null while it's still running. */
export async function readBuildResult(
  store: BlobStore,
  token: string,
): Promise<BuildResult | null> {
  const bytes = await store.get(`build/${token}`);
  if (!bytes) return null;
  const { ok, rev, error } = JSON.parse(dec.decode(bytes)) as {
    ok: boolean;
    rev?: string;
    error?: string;
  };
  return ok ? { ok: true, rev: rev ?? "" } : { ok: false, error: error ?? "" };
}
